import { useCallback, useEffect, useState } from "react";
import { send_request } from "../../utils/Request";
import useAgentStore from "./useAgentStore";
import {
  MASTER_AGENT_EXAMPLE_PROMPTS,
  MASTER_AGENT_NAME,
  MASTER_CHAT_UI_POLICY,
  PROVISION_ERROR_MESSAGE,
} from "./masterAgentDefaults";
import {
  extractRequestErrorMessage,
  isRequestError,
} from "./requestErrorUtils";

export default function useMasterAgentWorkspace() {
  const [agentId, setAgentId] = useState(null);
  const [agentName, setAgentName] = useState(MASTER_AGENT_NAME);
  const [agentDescription, setAgentDescription] = useState("");
  const [agentModel, setAgentModel] = useState("");
  const [agentInboxAddress, setAgentInboxAddress] = useState("");
  const [examplePrompts, setExamplePrompts] = useState(
    MASTER_AGENT_EXAMPLE_PROMPTS
  );
  const [chatUiPolicy, setChatUiPolicy] = useState(MASTER_CHAT_UI_POLICY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [taskQueueOpenSignal, setTaskQueueOpenSignal] = useState(0);
  const [taskQueueToggleVisible, setTaskQueueToggleVisible] = useState(false);
  const [taskQueuePanelOpen, setTaskQueuePanelOpen] = useState(false);
  const [sessionSidebarCollapsed, setSessionSidebarCollapsed] = useState(true);

  const loadAgent = useCallback(async (nextAgentId) => {
    const detail = await useAgentStore.getState().fetchAgent(nextAgentId);
    if (!detail) return;
    setAgentName(detail.name || MASTER_AGENT_NAME);
    setAgentDescription(detail.description || "");
    setAgentModel(detail.model || "");
    setAgentInboxAddress(detail.inboxAddress || "");
    setExamplePrompts(
      Array.isArray(detail.examplePrompts)
        ? detail.examplePrompts
        : MASTER_AGENT_EXAMPLE_PROMPTS
    );
    setChatUiPolicy({
      ...MASTER_CHAT_UI_POLICY,
      ...(detail.chatUiPolicy || {}),
      showTaskQueuePlannerInChat:
        detail.chatUiPolicy?.showTaskQueuePlannerInChat !== false,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const provisioned = await send_request(
          "project-service/agent/master/provision",
          {},
          {},
          "POST"
        );
        if (isRequestError(provisioned)) {
          throw new Error(
            extractRequestErrorMessage(provisioned, PROVISION_ERROR_MESSAGE)
          );
        }
        const nextAgentId = provisioned?.data?.agentId;
        if (!nextAgentId) {
          throw new Error("Master Agent provisioning returned no agentId");
        }
        if (cancelled) return;
        setAgentId(nextAgentId);
        setAgentName(provisioned?.data?.name || MASTER_AGENT_NAME);
        setAgentDescription(provisioned?.data?.description || "");
        await loadAgent(nextAgentId);
        useAgentStore.getState().fetchSessions(nextAgentId);
      } catch (e) {
        if (!cancelled) {
          setError(extractRequestErrorMessage(e, PROVISION_ERROR_MESSAGE));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadAgent]);

  const handleConfigSaved = useCallback(
    async (data) => {
      if (data?.name) setAgentName(data.name);
      if (data?.description != null) setAgentDescription(data.description);
      if (data?.inboxAddress) setAgentInboxAddress(data.inboxAddress);
      if (Array.isArray(data?.examplePrompts))
        setExamplePrompts(data.examplePrompts);
      if (agentId) {
        await loadAgent(agentId);
      }
    },
    [agentId, loadAgent]
  );

  const handleTaskQueueToggleStateChange = useCallback((state = {}) => {
    setTaskQueueToggleVisible(Boolean(state.visible));
    setTaskQueuePanelOpen(Boolean(state.open));
  }, []);

  const openTaskQueue = useCallback(() => {
    setTaskQueueOpenSignal((prev) => prev + 1);
  }, []);

  return {
    agentId,
    agentName,
    agentDescription,
    agentModel,
    agentInboxAddress,
    examplePrompts,
    chatUiPolicy,
    loading,
    error,
    configOpen,
    setConfigOpen,
    taskQueueOpenSignal,
    setTaskQueueOpenSignal,
    openTaskQueue,
    taskQueueToggleVisible,
    taskQueuePanelOpen,
    sessionSidebarCollapsed,
    setSessionSidebarCollapsed,
    handleConfigSaved,
    handleTaskQueueToggleStateChange,
  };
}
